import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { TYPE } from 'theme';
import { Tooltip } from '@mui/material';

const Card = styled.div`
    position: relative;
    background: #121212;
    border-radius: 8px;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 20px;
    min-height: 240px;
`;

const ImageWrapper = styled.div`
    position: relative;
    width: 100%;
    aspect-ratio: 2/3;
    border-radius: 8px;
    overflow: hidden;
    background: #1a1a1a;
    z-index: 1;
`;

const NFTImage = styled.img<{ $isLoaded: boolean }>`
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
    opacity: ${({ $isLoaded }) => ($isLoaded ? 1 : 0)};
    transition: opacity 0.3s ease-in-out;
`;

const Placeholder = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 8px;
    background: #121212;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: 8px;
        padding: 1px;
        background: linear-gradient(45deg, rgba(247, 147, 26, 0.4) 0%, rgba(247, 147, 26, 0) 100%);
        -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
        -webkit-mask-composite: xor;
        mask-composite: exclude;
    }

    &::after {
        content: '';
        position: absolute;
        inset: -150%;
        background: repeating-linear-gradient(
            -45deg,
            transparent 0%,
            transparent 35%,
            rgba(106, 13, 173, 0.3) 40%,
            transparent 45%,
            transparent 47%,
            rgba(106, 13, 173, 0.3) 52%,
            transparent 57%,
            transparent 100%
        );
        background-size: 150% 150%;
        animation: shine 2s ease-in-out infinite;
    }

    @keyframes shine {
        0% {
            background-position: 120% 120%;
        }
        100% {
            background-position: 30% 30%;
        }
    }
`;

const NFTInfo = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    position: relative;
    z-index: 1;
    align-items: center;
    width: 100%;
`;

const NameWrapper = styled.div`
    width: 100px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex-shrink: 0;
    display: inline-block;
`;

const StyledText = styled(TYPE.main)`
    display: block;
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

interface NFTCardProps {
    id: string;
    name: string;
    number: string;
    image?: string;
}

const NFTCard: React.FC<NFTCardProps> = ({ name, number, image }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);
    const cardRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setIsVisible(true);
                        observer.unobserve(entry.target);
                    }
                });
            },
            {
                rootMargin: '50px',
                threshold: 0.1,
            },
        );

        const currentRef = cardRef.current;
        if (currentRef) {
            observer.observe(currentRef);
        }

        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, []);

    return (
        <Card ref={cardRef}>
            {isVisible ? (
                <>
                    <ImageWrapper>
                        {!imageLoaded && <Placeholder />}
                        <NFTImage
                            src={image}
                            alt={name}
                            loading="lazy"
                            $isLoaded={imageLoaded}
                            onLoad={() => setImageLoaded(true)}
                        />
                    </ImageWrapper>
                    <NFTInfo>
                        <Tooltip title={name} arrow>
                            <NameWrapper>
                                <StyledText fontSize={16} fontWeight={600}>
                                    {name}
                                </StyledText>
                            </NameWrapper>
                        </Tooltip>
                        <TYPE.desc
                            fontSize={14}
                            color="#000"
                            style={{
                                background: '#F7931A',
                                padding: '4px 8px',
                                fontWeight: 400,
                                borderRadius: '8px',
                            }}
                        >
                            {number}
                        </TYPE.desc>
                    </NFTInfo>
                </>
            ) : null}
        </Card>
    );
};

export default NFTCard;
